/**
 * The Home lifecycle trail, as data: what each of an RFQ's eight boxes says and where it goes.
 * Pure and I/O-free; `LifecycleTrail.tsx` only draws what this returns.
 *
 * There is no server twin. The server decides WHICH documents belong to an RFQ
 * (src/utils/rfqLifecycle.js); this decides how they read.
 *
 * Boxes are told apart by what the server said, never guessed (BUSINESS_RULES#chain-hop-empty-merely-loaded):
 *   loading      the lifecycle has not arrived yet
 *   unavailable  it failed, or the server did not return this RFQ
 *   hidden       the caller may not see this stage (the stage is absent from the answer)
 *   empty        the caller may see it and there is nothing: "Not started" (credit notes: "No record found")
 *   filled       one or more documents
 */
import { toneForStatus } from '../../components/StatusBadge';
import type { Tone } from '../../components/ui/tokens';
import { isPseudoVendorName } from '../../utils/poSendStatus';
import { CHAIN_PATH } from '../traceability/chainRef';
import type { LifecycleDocDto, LifecycleDto, LifecycleStageKey } from '../../lib/rfqLifecycleApi';

export type BoxKey = 'rfq' | LifecycleStageKey;
export type BoxState = 'loading' | 'unavailable' | 'hidden' | 'empty' | 'filled';

/** The permission screens whose `view` lets a person OPEN a document (the pages' own route guards). */
export type LinkScreen = 'quotations' | 'confirmed_orders' | 'vendor_purchase_orders' | 'invoices';

interface StageSpec {
  key: BoxKey;
  label: string;
  /** What an empty box says. Credit notes are exceptions, never "started", so they say what is true. */
  empty: string;
  linkScreen: LinkScreen;
  path: (id: string) => string;
  /** What `n` documents of this stage are called in the box's small line. */
  unit: (n: number) => string;
}

const records = (n: number) => `${n} records`;

export const STAGE_SPECS: readonly StageSpec[] = [
  { key: 'rfq', label: 'RFQ', empty: 'Not started', linkScreen: 'quotations', path: CHAIN_PATH.rfq, unit: records },
  { key: 'quotation', label: 'Quotation', empty: 'Not started', linkScreen: 'quotations', path: CHAIN_PATH.quotation, unit: records },
  { key: 'order', label: 'Order', empty: 'Not started', linkScreen: 'confirmed_orders', path: CHAIN_PATH.confirmedOrder, unit: records },
  { key: 'purchaseOrders', label: 'Vendor PO', empty: 'Not started', linkScreen: 'vendor_purchase_orders', path: CHAIN_PATH.purchaseOrder, unit: (n) => `${n} POs` },
  { key: 'vendorInvoices', label: 'Vendor Invoice', empty: 'Not started', linkScreen: 'invoices', path: CHAIN_PATH.vendorInvoice, unit: (n) => `${n} invoices` },
  { key: 'clientInvoices', label: 'Client Invoice', empty: 'Not started', linkScreen: 'invoices', path: CHAIN_PATH.clientInvoice, unit: (n) => `${n} invoices` },
  { key: 'creditNotes', label: 'Credit Note', empty: 'No record found', linkScreen: 'invoices', path: CHAIN_PATH.creditNote, unit: records },
  // There is no payment record, only the client invoice's own status, so a payment opens its invoice.
  { key: 'payments', label: 'Payment', empty: 'Not started', linkScreen: 'invoices', path: CHAIN_PATH.clientInvoice, unit: (n) => `${n} invoices` },
];

/** Stage keys the server returns, i.e. every box but the RFQ itself. */
export const SERVER_STAGES: readonly LifecycleStageKey[] = STAGE_SPECS.filter((s) => s.key !== 'rfq').map(
  (s) => s.key as LifecycleStageKey
);

export interface BoxDoc {
  id: string;
  /** The line a person reads in the list: the vendor for a PO / vendor invoice, else the number. */
  title: string;
  /** Smaller line under it: the number (for vendor documents) and the status in words. */
  detail: string;
  tone: Tone;
  to: string;
}

export interface TrailBox {
  key: BoxKey;
  label: string;
  state: BoxState;
  tone: Tone;
  /** The bold line. For `empty`/`hidden`/... the sentence that says so. */
  value: string;
  /** The small line under it; '' when there is none. */
  sub: string;
  docs: BoxDoc[];
}

/** The stored status as a person reads it: "partially_paid" -> "Partially paid". */
export function statusWords(status: string): string {
  const s = String(status || '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** A payment is the client invoice's own state, worded as a payment. */
const PAYMENT_WORDS: Record<string, string> = {
  sent: 'Awaiting payment',
  partially_paid: 'Part paid',
  paid: 'Paid',
};
export const paymentWords = (status: string): string => PAYMENT_WORDS[status] || statusWords(status);

/**
 * A payment's tone is set here, not read off its words: "Part paid" contains "paid" and would classify
 * as done. Sent or part paid means money is still owed (attention); only paid is finished.
 */
const PAYMENT_TONE: Record<string, Tone> = { sent: 'attention', partially_paid: 'attention', paid: 'positive' };
const docTone = (key: BoxKey, status: string): Tone =>
  key === 'payments' ? PAYMENT_TONE[status] || 'neutral' : toneForStatus(status);

/** Worst tone first: the box dot shows the most urgent thing inside it. */
const SEVERITY: Tone[] = ['overdue', 'negative', 'attention', 'neutral', 'positive'];
export function worstTone(tones: Tone[]): Tone {
  for (const t of SEVERITY) if (tones.includes(t)) return t;
  return 'neutral';
}

const isCancelled = (status: string) => status.trim().toLowerCase() === 'cancelled';

/** The real vendor names of a stage's documents, once each, in order. Placeholder names are not vendors. */
function vendorNames(docs: LifecycleDocDto[]): string[] {
  const seen: string[] = [];
  docs.forEach((d) => {
    const name = (d.vendorName || '').trim();
    if (name && !isPseudoVendorName(name) && !seen.includes(name)) seen.push(name);
  });
  return seen;
}

function boxDocs(spec: StageSpec, docs: LifecycleDocDto[]): BoxDoc[] {
  const byVendor = spec.key === 'purchaseOrders' || spec.key === 'vendorInvoices';
  return docs.map((d) => {
    const vendor = byVendor ? vendorNames([d])[0] : '';
    const number = d.number || '(no number)';
    const words = spec.key === 'payments' ? paymentWords(d.status) : statusWords(d.status);
    return {
      id: d.id,
      title: vendor || number,
      detail: [vendor ? number : '', words].filter(Boolean).join(' · '),
      tone: docTone(spec.key, d.status),
      to: spec.path(d.id),
    };
  });
}

function filledBox(spec: StageSpec, docs: LifecycleDocDto[]): TrailBox {
  const items = boxDocs(spec, docs);
  const tone = worstTone(items.map((d) => d.tone));
  const vendorStage = spec.key === 'purchaseOrders' || spec.key === 'vendorInvoices';
  const names = vendorStage ? vendorNames(docs) : [];

  let value: string;
  let sub = '';
  if (docs.length === 1) {
    if (spec.key === 'payments') {
      value = paymentWords(docs[0].status);
      sub = docs[0].number;
    } else if (vendorStage && names.length) {
      value = names[0];
      sub = docs[0].number;
    } else {
      value = docs[0].number || '(no number)';
    }
  } else if (vendorStage && names.length) {
    value = names.length > 1 ? `${names[0]} +${names.length - 1}` : names[0];
    sub = spec.unit(docs.length);
  } else {
    value = spec.unit(docs.length);
  }
  return { key: spec.key, label: spec.label, state: 'filled', tone, value, sub, docs: items };
}

/**
 * D161.3: once the RFQ has a confirmed order its quotation is Confirmed, as the quotation list and
 * header say. With no order stage (no access) the stored status stands: nothing is guessed.
 */
function withOrderedQuotation(lifecycle: LifecycleDto): LifecycleDto {
  if (!lifecycle.quotation || !(lifecycle.order || []).some((d) => d.status === 'confirmed')) return lifecycle;
  return { ...lifecycle, quotation: lifecycle.quotation.map((d) => ({ ...d, status: 'confirmed' })) };
}

export type LifecycleInput = LifecycleDto | 'loading' | 'unavailable';

/** The eight boxes of one RFQ's trail. `lifecycle` is what the server said about it, or why it has none yet. */
export function buildTrail(
  rfq: { _id: string; rfqId?: string; status?: string },
  lifecycle: LifecycleInput
): TrailBox[] {
  return STAGE_SPECS.map((spec): TrailBox => {
    const base = { key: spec.key, label: spec.label, docs: [] as BoxDoc[], sub: '' };

    if (spec.key === 'rfq') {
      const number = rfq.rfqId || '(no number)';
      return {
        ...base,
        state: 'filled',
        tone: toneForStatus(rfq.status),
        value: number,
        docs: [
          {
            id: rfq._id,
            title: number,
            detail: statusWords(rfq.status || ''),
            tone: toneForStatus(rfq.status),
            to: spec.path(rfq._id),
          },
        ],
      };
    }
    if (lifecycle === 'loading') return { ...base, state: 'loading', tone: 'neutral', value: '' };
    if (lifecycle === 'unavailable') return { ...base, state: 'unavailable', tone: 'neutral', value: 'Unavailable' };

    const docs = withOrderedQuotation(lifecycle)[spec.key as LifecycleStageKey];
    if (docs === undefined) return { ...base, state: 'hidden', tone: 'neutral', value: 'No access' };
    if (!docs.length) return { ...base, state: 'empty', tone: 'neutral', value: spec.empty };
    return filledBox(spec, docs);
  });
}

export type RowState = 'attention' | 'open' | 'completed';

/**
 * Where an RFQ stands, from its documents.
 *   attention  something downstream is waiting on a person or has gone wrong (a draft PO, a vendor invoice
 *              to review, a part-paid invoice, a failed read). Cancelled documents are history, not alerts.
 *   completed  it has client invoices and every one that is not cancelled is paid.
 *   open       anything else.
 * The RFQ box itself never counts: an RFQ "ready" for quoting is normal, not a problem.
 */
export function rowState(lifecycle: LifecycleDto): RowState {
  const shown = withOrderedQuotation(lifecycle);
  const live = SERVER_STAGES.flatMap((k) => shown[k] || []).filter((d) => !isCancelled(d.status));
  const tones = live.map((d) => toneForStatus(d.status));
  if (tones.some((t) => t === 'attention' || t === 'negative' || t === 'overdue')) return 'attention';
  const invoices = (lifecycle.clientInvoices || []).filter((d) => !isCancelled(d.status));
  if (invoices.length && invoices.every((d) => d.status === 'paid')) return 'completed';
  return 'open';
}

/** Words a person can search a row by: every number and vendor in it. Used with the RFQ's own fields. */
export function searchText(lifecycle: LifecycleInput): string {
  if (lifecycle === 'loading' || lifecycle === 'unavailable') return '';
  return SERVER_STAGES.flatMap((k) => lifecycle[k] || [])
    .flatMap((d) => [d.number, d.vendorName || ''])
    .join(' ')
    .toLowerCase();
}

/** The steps the sidebar trail shows: every box but Payment, which has no module of its own to open. */
export function sidebarSteps(boxes: TrailBox[]): TrailBox[] {
  return boxes.filter((b) => b.key !== 'payments');
}

const LOCK_NOTES: Partial<Record<BoxKey, string>> = {
  quotation: 'No quotation has been generated for this RFQ yet.',
  order: 'No order yet. An order is created when the quotation is confirmed.',
  purchaseOrders: 'No vendor PO yet',
  vendorInvoices: 'No vendor invoice yet',
  clientInvoices: 'No client invoice yet',
  creditNotes: 'No credit note yet',
};

/** What a locked step says when it is pressed: why there is nothing to open. */
export function lockNote(box: TrailBox): string {
  if (box.state === 'hidden') return `You do not have access to ${box.label.toLowerCase()} records.`;
  return LOCK_NOTES[box.key] || `${box.label} has not been created yet.`;
}

export interface LegendEntry {
  tone: Tone;
  label: string;
  /** A real status that gets this colour, run through `toneForStatus` by the test so the key cannot drift. */
  example: string;
}

/** What each dot colour on the Home trail means. The words, not the colour, are the message. */
export const LEGEND: readonly LegendEntry[] = [
  { tone: 'positive', label: 'Done', example: 'Confirmed' },
  { tone: 'attention', label: 'Needs attention (waiting on someone)', example: 'Draft' },
  { tone: 'negative', label: 'Cancelled, failed or overdue', example: 'Cancelled' },
  { tone: 'neutral', label: 'Nothing to act on', example: 'Finalized' },
];
